import { DealRowActionType } from '@openx/components-deal/constants.ts';
import type { DealRowActionPayload } from '@openx/components-deal/lib/utils/context/DealHookTypes.ts';
import type { TFunction } from 'i18next';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

export type SupportedDealActions =
  | typeof DealRowActionType.CLONE
  | typeof DealRowActionType.DELETE
  | typeof DealRowActionType.PAUSE
  | typeof DealRowActionType.RESUME;

export type PerformActionParam<T = void, V extends DealRowActionPayload = DealRowActionPayload> = {
  action: SupportedDealActions;
  deals: V[];
  actionHandler: (deal: V) => Promise<T>;
  getErrorMessages?: (error: unknown) => string[];
};

export const useDealsAction = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const performDealAction = async <T, V extends DealRowActionPayload = DealRowActionPayload>({
    action,
    deals,
    actionHandler,
    getErrorMessages,
  }: PerformActionParam<T, V>): Promise<{
    fulfilledIds: string[];
    rejectedIds: string[];
    successfulResults: T[];
    message: string;
  }> => {
    const results = await Promise.allSettled(deals.map(async deal => actionHandler(deal)));

    const fulfilledIds: string[] = [];
    const rejectedIds: string[] = [];
    const rejectedErrors: unknown[] = [];
    const successfulResults: T[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        fulfilledIds.push(deals[index].id);
        successfulResults.push(result.value);
      } else {
        rejectedIds.push(deals[index].id);
        rejectedErrors.push(result.reason);
      }
    });

    const dealsCount = deals.length;
    const rejectedCount = rejectedIds.length;

    const { message, variant } = getDealActionFeedbackMessage({ action, dealsCount, rejectedCount, t });
    const errorMessages = [...new Set(rejectedErrors.flatMap(error => getErrorMessages?.(error) ?? []))];

    if (deals.length > 1 || !errorMessages.length) {
      enqueueSnackbar(message, { variant });
    }

    errorMessages.forEach(errorMessage => {
      enqueueSnackbar(errorMessage, { variant: 'error' });
    });

    return Promise.resolve({ fulfilledIds, message, rejectedIds, successfulResults });
  };

  return { performDealAction };
};

export const getDealActionFeedbackMessage = ({
  action,
  t,
  dealsCount,
  rejectedCount,
}: {
  action: SupportedDealActions;
  t: TFunction<'translation', undefined>;
  dealsCount: number;
  rejectedCount: number;
}): { message: string; variant: 'success' | 'error' } => {
  const dealPluralityLabel = dealsCount > 1 ? t('Deals') : t('Deal');

  const translationArg = {
    dealPluralityLabel,
    dealsCount,
    rejectedCount,
  };

  // all deals successfully updated
  if (rejectedCount === 0) {
    return {
      message: t('{dealPluralityLabel} successfully {actionLabel}.', {
        ...translationArg,
        actionLabel: getDealActionLabel(action, t),
      }),
      variant: 'success',
    };
  }

  // if only one deal was selected and get an error
  if (dealsCount === 1) {
    return {
      message: t('There was an error {actionLabel} {dealPluralityLabel}.', {
        ...translationArg,
        actionLabel: getDealActionLabel(action, t, true),
        dealPluralityLabel: dealPluralityLabel.toLowerCase(),
      }),
      variant: 'error',
    };
  }

  // if multiple deals are selected and get an error
  return {
    message: t('There was an error {actionLabel} {dealPluralityLabel}. {rejectedCount} out of {dealsCount} failed.', {
      ...translationArg,
      actionLabel: getDealActionLabel(action, t, true),
      dealPluralityLabel: dealPluralityLabel.toLowerCase(),
    }),
    variant: 'error',
  };
};

export const getDealActionLabel = (action: SupportedDealActions, t, isContinuous = false): string => {
  const continuous = {
    [DealRowActionType.CLONE]: 'cloning',
    [DealRowActionType.DELETE]: 'deleting',
    [DealRowActionType.PAUSE]: 'pausing',
    [DealRowActionType.RESUME]: 'resuming',
  };

  const finished = {
    [DealRowActionType.CLONE]: 'cloned',
    [DealRowActionType.DELETE]: 'deleted',
    [DealRowActionType.PAUSE]: 'paused',
    [DealRowActionType.RESUME]: 'resumed',
  };

  const text = isContinuous ? continuous[action] : finished[action];

  return t(text);
};
